import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions,
  CircularProgress,
  Breadcrumbs,
  Link,
  Chip,
  Divider,
  InputAdornment,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import { 
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Gavel as BidIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { Invoice, BidCreate, ScoreGrade } from '../types/models';
import ScoreBadge from '../components/invoices/ScoreBadge';
import { formatCurrency, formatDate, formatDateOnly } from '../utils/formatters';

const Marketplace: React.FC = () => {
  const { user } = useAuth();
  
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Bid dialog
  const [openBidDialog, setOpenBidDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [bidAmount, setBidAmount] = useState<string>('');
  const [bidLoading, setBidLoading] = useState(false);
  const [bidError, setBidError] = useState<string | null>(null);
  const [bidSuccess, setBidSuccess] = useState(false);
  
  const fetchMarketplaceInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get<Invoice[]>('/invoices/marketplace');
      setInvoices(response);
    } catch (err: any) {
      console.error('Error fetching marketplace invoices:', err);
      setError(err.response?.data?.detail || 'Failed to load marketplace invoices');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchMarketplaceInvoices();
  }, []);
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleOpenBidDialog = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setBidAmount('');
    setBidError(null);
    setBidSuccess(false);
    setOpenBidDialog(true);
  };
  
  const handleCloseBidDialog = () => {
    setOpenBidDialog(false);
    // If bid was successful, refresh the marketplace
    if (bidSuccess) {
      fetchMarketplaceInvoices();
    }
  };
  
  const handleBidAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setBidAmount(value);
    }
  };
  
  const handleSubmitBid = async () => {
    if (!selectedInvoice) return;
    
    // Validate bid amount
    const numericBidAmount = parseFloat(bidAmount);
    if (isNaN(numericBidAmount) || numericBidAmount <= 0) {
      setBidError('Por favor ingresa un monto de oferta válido');
      return;
    }
    
    if (selectedInvoice.highest_bid && numericBidAmount <= selectedInvoice.highest_bid) {
      setBidError('Tu oferta debe ser mayor a la oferta más alta actual.');
      return;
    }

    if (numericBidAmount >= selectedInvoice.net_amount) {
      setBidError('El monto de la oferta debe ser menor al monto neto de la factura');
      return;
    }
    
    setBidLoading(true);
    setBidError(null);
    
    try {
      const bidData: BidCreate = {
        invoice_id: selectedInvoice.id,
        amount: numericBidAmount
      };
      
      await apiService.post('/bids', bidData);
      setBidSuccess(true);
    } catch (err: any) {
      console.error('Error placing bid:', err);
      setBidError(err.response?.data?.detail || 'Failed to place bid');
    } finally {
      setBidLoading(false);
    }
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
          Panel de Control
        </Link>
        <Typography color="text.primary">Mercado</Typography>
      </Breadcrumbs>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom component="div">
          Mercado
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<BidIcon />}
          component={RouterLink}
          to="/bids"
        >
          Ver Mis Ofertas
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : invoices.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">No hay facturas disponibles en el mercado</Typography>
          <Typography variant="body2" color="text.secondary">
            Vuelve más tarde para ver nuevas facturas
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
              <Table stickyHeader aria-label="marketplace invoices table">
                <TableHead>
                  <TableRow>
                    <TableCell>Empresa</TableCell>
                    <TableCell>Factura #</TableCell>
                    <TableCell>Pagador</TableCell>
                    <TableCell>Fecha de Emisión</TableCell>
                    <TableCell>Fecha de Vencimiento</TableCell>                    
                    <TableCell align="right">Monto Neto</TableCell>
                    <TableCell align="right">Oferta Más Alta</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invoice) => (
                      <TableRow key={invoice.id} hover>
                        <TableCell>
                          {invoice.issuer ? invoice.issuer.name : 'Desconocido'}
                        </TableCell>
                        <TableCell>{invoice.invoice_number}</TableCell>
                        <TableCell>
                          {invoice.debtor ? invoice.debtor.name : 'Deudor Desconocido'}
                        </TableCell>
                        <TableCell>{formatDateOnly(invoice.issue_date)}</TableCell>
                        <TableCell>{formatDateOnly(invoice.due_date)}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(invoice.net_amount, 'PYG')}
                        </TableCell>
                        <TableCell align="right">
                          {invoice.highest_bid != null ? formatCurrency(invoice.highest_bid, 'PYG') : 'Sin ofertas'}
                        </TableCell>
                        <TableCell>
                          {invoice.effective_grade ? (
                            <ScoreBadge
                              grade={invoice.effective_grade as ScoreGrade}
                              size="small"
                              overridden={invoice.score_source === 'override'}
                            />
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<BidIcon />}
                            onClick={() => handleOpenBidDialog(invoice)}
                          >
                            Hacer Oferta
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={invoices.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </>
      )}
      
      {/* Bid Dialog */}
      <Dialog open={openBidDialog} onClose={handleCloseBidDialog}>
        <DialogTitle>Hacer una Oferta</DialogTitle>
        <DialogContent>
          {bidSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              ¡Tu oferta ha sido enviada exitosamente!
            </Alert>
          ) : (
            <>
              {bidError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {bidError}
                </Alert>
              )}
              
              {selectedInvoice && (
                <>
                  <DialogContentText>
                    Estás haciendo una oferta en la factura #{selectedInvoice.invoice_number}
                  </DialogContentText>
                  
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Deudor
                        </Typography>
                        <Typography variant="body1">
                          {selectedInvoice.debtor ? selectedInvoice.debtor.name : 'Deudor Desconocido'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Monto Neto
                        </Typography>
                        <Typography variant="body1">
                          {formatCurrency(selectedInvoice.net_amount, selectedInvoice.currency)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Oferta Más Alta
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {selectedInvoice.highest_bid 
                            ? formatCurrency(selectedInvoice.highest_bid, selectedInvoice.currency) 
                            : 'Sin ofertas aún'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Fecha de Emisión
                        </Typography>
                        <Typography variant="body1">
                          {formatDateOnly(selectedInvoice.issue_date)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Fecha de Vencimiento
                        </Typography>
                        <Typography variant="body1">
                          {formatDateOnly(selectedInvoice.due_date)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <TextField
                    autoFocus
                    margin="dense"
                    id="bid-amount"
                    label="Monto de tu Oferta"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={bidAmount}
                    onChange={handleBidAmountChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MoneyIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {selectedInvoice.currency}
                        </InputAdornment>
                      ),
                    }}
                    helperText="Ingresa un monto menor al monto de la factura"
                    disabled={bidLoading}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBidDialog}>
            {bidSuccess ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!bidSuccess && (
            <Button 
              onClick={handleSubmitBid} 
              variant="contained" 
              disabled={bidLoading}
              startIcon={bidLoading ? <CircularProgress size={20} /> : <BidIcon />}
            >
              {bidLoading ? 'Enviando Oferta...' : 'Hacer Oferta'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Marketplace;
